import { useMemo } from 'react';
import { BarChart3, TrendingUp, DollarSign } from 'lucide-react';
import type { Project, Criterion } from '../types';

interface ResultsViewProps {
  projects: Project[];
  criteria: Criterion[];
  goal: string;
  onReset: () => void;
}

export function ResultsView({ projects, criteria, goal, onReset }: ResultsViewProps) {
  // Sort projects by final score (descending)
  const sortedProjects = useMemo(() => {
    return [...projects].sort((a, b) => b.finalScore - a.finalScore);
  }, [projects]);

  // Calculate efficient frontier (projects with best value/cost ratio)
  const efficientProjects = useMemo(() => {
    return sortedProjects
      .filter(p => p.cost > 0)
      .sort((a, b) => b.efficiencyScore - a.efficiencyScore)
      .slice(0, 5);
  }, [sortedProjects]);

  // Get top contributing criteria for a project
  const getTopCriteria = (project: Project) => {
    return criteria
      .map(c => ({
        criterion: c,
        contribution: c.globalWeight * (project.scores[c.id] || 0),
      }))
      .sort((a, b) => b.contribution - a.contribution)
      .slice(0, 3);
  };

  // Calculate max values for scaling
  const maxValue = Math.max(...projects.map(p => p.finalScore), 1);
  const maxCost = Math.max(...projects.map(p => p.cost), 1);

  return (
    <div className="max-w-6xl mx-auto space-y-8">
      {/* Header */}
      <div>
        <h2 className="text-3xl font-bold text-[var(--text-pri)] mb-2">Results: {goal}</h2>
        <p className="text-[var(--text-sec)]">
          Projects ranked by strategic value based on your criteria weights
        </p>
      </div>

      {/* Summary Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-[var(--bg-raised)] border border-[var(--rule)] p-4">
          <div className="flex items-center gap-3">
            <BarChart3 className="h-8 w-8 text-consulting-slate-blue-600" />
            <div>
              <div className="text-2xl font-bold text-[var(--text-pri)]">{projects.length}</div>
              <div className="text-sm text-[var(--text-sec)]">Projects Evaluated</div>
            </div>
          </div>
        </div>
        <div className="bg-[var(--bg-raised)] border border-[var(--rule)] p-4">
          <div className="flex items-center gap-3">
            <TrendingUp className="h-8 w-8 text-[var(--green)]" />
            <div>
              <div className="text-2xl font-bold text-[var(--text-pri)]">
                {sortedProjects[0]?.name || 'N/A'}
              </div>
              <div className="text-sm text-[var(--text-sec)]">Top Priority</div>
            </div>
          </div>
        </div>
        <div className="bg-[var(--bg-raised)] border border-[var(--rule)] p-4">
          <div className="flex items-center gap-3">
            <DollarSign className="h-8 w-8 text-[var(--text-ter)]" />
            <div>
              <div className="text-2xl font-bold text-[var(--text-pri)]">
                {efficientProjects[0]?.name || 'N/A'}
              </div>
              <div className="text-sm text-[var(--text-sec)]">Best Value</div>
            </div>
          </div>
        </div>
      </div>

      {/* Cost-Value Matrix (Efficient Frontier) */}
      <div className="bg-[var(--bg-raised)] border border-[var(--rule)] p-6">
        <h3 className="text-xl font-bold text-[var(--text-pri)] mb-4">Cost-Value Matrix</h3>
        <p className="text-sm text-[var(--text-sec)] mb-6">
          Projects positioned by strategic value (Y-axis) and cost/effort (X-axis). 
          Focus on high-value, low-cost projects first.
        </p>
        
        <div className="relative" style={{ height: '400px' }}>
          {/* Y-axis label */}
          <div className="absolute left-0 top-1/2 -translate-y-1/2 -rotate-90 text-sm font-medium text-[var(--text-sec)] whitespace-nowrap">
            Strategic Value
          </div>
          
          {/* Chart area */}
          <div className="ml-12 mr-8 mt-8 mb-12 h-80 border-l border-b border-[var(--rule)] relative">
            {/* Quadrant labels */}
            <div className="absolute top-2 left-2 text-xs font-medium text-[var(--green)] dark:text-[var(--text-ter)]">
              High Value / Low Cost (Do First)
            </div>
            <div className="absolute top-2 right-2 text-xs font-medium text-[var(--text-ter)]">
              High Value / High Cost (Consider)
            </div>
            <div className="absolute bottom-2 left-2 text-xs font-medium text-[var(--text-ter)]">
              Low Value / Low Cost (Maybe)
            </div>
            <div className="absolute bottom-2 right-2 text-xs font-medium text-[var(--coral)]">
              Low Value / High Cost (Avoid)
            </div>

            {/* Projects as dots */}
            {projects.map((project, index) => {
              const x = (project.cost / maxCost) * 100;
              const y = (project.finalScore / maxValue) * 100;
              const isTop = index < 3;
              
              return (
                <div
                  key={project.id}
                  className="absolute group cursor-pointer"
                  style={{
                    left: `${x}%`,
                    bottom: `${y}%`,
                    transform: 'translate(-50%, 50%)',
                  }}
                >
                  <div
                    className={`w-4 h-4 rounded-full border-2 ${
                      isTop
                        ? 'bg-consulting-slate-blue-600 border-consulting-slate-blue-800'
                        : 'bg-[var(--surface)] border-[var(--rule)]'
                    } hover:scale-150 transition-transform`}
                  />
                  <div className="absolute left-1/2 top-6 -translate-x-1/2 opacity-0 group-hover:opacity-100 transition-opacity bg-[var(--bg)] text-white text-xs px-2 py-1 rounded whitespace-nowrap z-10">
                    {project.name}
                    <br />
                    <span className="text-[var(--text-ter)]">
                      Value: {project.finalScore.toFixed(2)} | Cost: {project.cost}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>

          {/* X-axis label */}
          <div className="text-center mt-2 text-sm font-medium text-[var(--text-sec)]">
            Cost / Effort
          </div>
        </div>
      </div>

      {/* Ranked Projects List */}
      <div className="bg-[var(--bg-raised)] border border-[var(--rule)] p-6">
        <h3 className="text-xl font-bold text-[var(--text-pri)] mb-4">Ranked Projects</h3>
        <div className="space-y-4">
          {sortedProjects.map((project, index) => {
            const topCriteria = getTopCriteria(project);
            
            return (
              <div
                key={project.id}
                className={`p-4 border-2  ${
                  index === 0
                    ? 'border-consulting-slate-blue-500 bg-consulting-slate-blue-50'
                    : 'border-[var(--rule)] bg-[var(--bg-raised)]'
                }`}
              >
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-start gap-4">
                    <div className={`w-12 h-12 rounded-full flex items-center justify-center font-bold text-lg ${
                      index === 0
                        ? 'bg-consulting-slate-blue-600 text-white'
                        : index === 1
                        ? 'bg-[var(--surface)] text-white'
                        : index === 2
                        ? 'bg-[var(--mustard)] text-white'
                        : 'bg-[var(--rule-bright)] text-[var(--text-sec)]'
                    }`}>
                      {index + 1}
                    </div>
                    <div>
                      <h4 className="text-lg font-bold text-[var(--text-pri)]">{project.name}</h4>
                      <div className="flex items-center gap-4 mt-1 text-sm text-[var(--text-sec)]">
                        <span>Value: <strong>{project.finalScore.toFixed(3)}</strong></span>
                        <span>Cost: <strong>{project.cost}</strong></span>
                        <span>Efficiency: <strong>{(project.efficiencyScore * 100).toFixed(1)}%</strong></span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Top contributing criteria */}
                <div className="ml-16 mt-3">
                  <p className="text-xs font-medium text-[var(--text-ter)] mb-2">Top Contributing Criteria:</p>
                  <div className="flex flex-wrap gap-2">
                    {topCriteria.map(({ criterion, contribution }) => (
                      <span
                        key={criterion.id}
                        className="px-2 py-1 bg-[var(--hover-tint)] text-[var(--text-sec)] text-xs rounded"
                      >
                        {criterion.name} ({((contribution / project.finalScore) * 100).toFixed(0)}%)
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Actions */}
      <div className="flex justify-end pt-6 border-t border-[var(--rule)]">
        <button
          onClick={onReset}
          className="px-6 py-3 bg-[var(--hover-tint)] text-[var(--text-sec)] hover:bg-[var(--rule-bright)] font-semibold"
        >
          Start New Analysis
        </button>
      </div>
    </div>
  );
}

